// ViewerSync: the viewer's read-only sync client. Deliberately built from
// @cairn/core's lower-level pieces — Provider + DocStore + MetaView — rather
// than SyncEngine, whose Bridge mirrors a disk vault the viewer doesn't have
// (decisions/22-web-viewer.md). Subscriptions are lazy LIVE: the meta doc for
// the whole vault view, plus exactly one content doc for the open file.
// Presence is name-only awareness (web-viewer spec §Behavior).

import * as Y from "yjs";
import {
  DocStore,
  Emitter,
  HistoryClient,
  META_DOC_ID,
  MetaView,
  Provider,
  yprotocol,
  type ClockPort,
  type NetPort,
  type SyncStatus,
} from "@cairn/core";
import { Mode, Reason, Role } from "@cairn/proto";
import { MemPersistence } from "./memPersistence";

/** Human phrase for why the server rejected the meta doc during open. */
function reasonText(reason: Reason): string {
  switch (reason) {
    case Reason.TOO_LARGE:
      return "the vault index is too large to load";
    case Reason.INVALID:
      return "the vault index is invalid or corrupt";
    case Reason.RATE_LIMITED:
      return "the server is rate-limiting this connection — wait a moment and retry";
    case Reason.NOT_FOUND:
      return "the vault's data was not found on the server";
    case Reason.EPOCH_STALE:
      return "the vault's data changed while loading";
    default:
      return "the server rejected the vault index";
  }
}

/**
 * Thrown from {@link ViewerSync.start} when the meta doc fails to sync AND the
 * server gave a concrete rejection reason — so the UI can show *why* instead of
 * a bare "did not sync within Nms" timeout.
 */
export class MetaSyncError extends Error {
  constructor(readonly reason: Reason) {
    super(reasonText(reason));
    this.name = "MetaSyncError";
  }
}

export interface ViewerSyncOptions {
  vaultId: string;
  /** `ws(s)://<host>/sync/<vault-id>` from the token mint response. */
  wsUrl: string;
  /** Mints a fresh sync token (POST /vaults/:id/token, cookie + CSRF). */
  mintToken: () => Promise<string>;
  /** Awareness presence: name only, no cursor. */
  displayName: string;
}

const realClock: ClockPort = {
  now: () => Date.now(),
  timer: (ms) => new Promise((r) => setTimeout(r, ms)),
};

/** A remote member's presence on the open doc. */
export interface PresenceEntry {
  clientId: number;
  name: string;
}

export class ViewerSync {
  readonly docs: DocStore;
  readonly provider: Provider;
  readonly history: HistoryClient;

  /** Fires on any change to sync status. */
  readonly statusChanged = new Emitter<SyncStatus>();
  /** Fires when the server reports (or changes) this connection's role. */
  readonly roleChanged = new Emitter<Role>();

  private role: Role = Role.ROLE_UNSPECIFIED;
  /** Last server rejection of the meta doc, surfaced if the open times out. */
  private lastMetaRejection: Reason | null = null;
  /** Content docs currently open (at most one per spec, but kept general). */
  private readonly openDocs = new Set<string>();
  /** Awareness listeners to detach on close, keyed by docId. */
  private readonly awarenessDetach = new Map<string, () => void>();

  constructor(
    net: NetPort,
    private readonly opts: ViewerSyncOptions,
    clock: ClockPort = realClock,
  ) {
    this.docs = new DocStore(new MemPersistence());
    this.history = new HistoryClient(net.http, opts.vaultId);
    this.provider = new Provider(
      net,
      clock,
      { wsUrl: opts.wsUrl, mintToken: opts.mintToken },
      {
        onAuthAck: (_userId, role) => {
          this.setRole(role);
          // Server-side awareness state died with the old socket; re-announce.
          for (const docId of this.openDocs) this.announce(docId);
        },
        onStatus: (status) => this.statusChanged.emit(status),
        onSubscribeAck: (docId, epoch, epochChanged) => {
          void this.docs.onSubscribeAck(docId, epoch, epochChanged);
        },
        onDocPayload: (docId, payload) => this.docs.onDocPayload(docId, payload),
        onRejected: (docId, reason, detail) => this.docs.onRejected(docId, reason, detail),
        onDocCreated: () => {}, // meta-doc update carries the change
        onDocRemoved: () => {},
        onRoleChanged: (role) => this.setRole(role),
        subscribeParams: (docId) => this.docs.subscribeParams(docId),
      },
    );
    this.docs.attach(this.provider);
    // Remember the reason for any meta-doc rejection so a subsequent open
    // timeout can report the underlying cause instead of a bare timeout.
    this.docs.rejected.on(({ docId, reason }) => {
      if (docId === META_DOC_ID) this.lastMetaRejection = reason;
    });
    // An epoch bump swaps the Y.Doc and its Awareness; re-wire presence.
    this.docs.epochChanged.on((docId) => {
      if (this.openDocs.has(docId)) this.announce(docId);
    });
  }

  get currentRole(): Role {
    return this.role;
  }

  /** Restore is editor+ (history spec §UI). */
  canRestore(): boolean {
    return this.role === Role.ROLE_EDITOR || this.role === Role.ROLE_OWNER;
  }

  /** Connect and LIVE-subscribe the meta doc; resolves once meta is synced.
   *  If the sync times out but the server had rejected the meta doc, throws a
   *  {@link MetaSyncError} carrying the reason so the UI can explain the cause. */
  async start(): Promise<void> {
    this.lastMetaRejection = null;
    await this.docs.subscribe(META_DOC_ID, Mode.MODE_LIVE);
    this.provider.connect();
    try {
      await this.docs.waitSynced(META_DOC_ID);
    } catch (timeout) {
      const reason = this.lastMetaRejection;
      if (reason !== null && reason !== Reason.REASON_UNSPECIFIED) {
        throw new MetaSyncError(reason);
      }
      throw timeout;
    }
  }

  meta(): MetaView | undefined {
    const doc = this.docs.getDoc(META_DOC_ID);
    return doc ? new MetaView(doc) : undefined;
  }

  /** LIVE-subscribe one content doc and announce presence on it. */
  async openFile(docId: string): Promise<Y.Doc> {
    const doc = await this.docs.subscribe(docId, Mode.MODE_LIVE);
    this.openDocs.add(docId);
    this.announce(docId);
    await this.docs.waitSynced(docId);
    return doc;
  }

  /** Withdraw presence and drop the content doc (leaving the file view). */
  async closeFile(docId: string): Promise<void> {
    this.openDocs.delete(docId);
    const detach = this.awarenessDetach.get(docId);
    if (detach) {
      detach();
      this.awarenessDetach.delete(docId);
    }
    const aw = this.docs.awareness(docId);
    if (aw) {
      aw.setLocalState(null); // broadcast removal before dropping
      this.sendAwareness(docId, aw);
    }
    await this.docs.drop(docId);
  }

  /** Remote presence entries on a doc (name only), excluding ourselves. */
  presence(docId: string): PresenceEntry[] {
    const aw = this.docs.awareness(docId);
    if (!aw) return [];
    const out: PresenceEntry[] = [];
    aw.getStates().forEach((state, clientId) => {
      if (clientId === aw.clientID) return;
      const user = (state as { user?: { name?: unknown } }).user;
      const name = typeof user?.name === "string" ? user.name : "unknown";
      out.push({ clientId, name });
    });
    out.sort((a, b) => a.clientId - b.clientId);
    return out;
  }

  /** Subscribe to awareness changes on a doc; returns a detach fn. */
  onPresence(docId: string, cb: () => void): () => void {
    const attach = (): (() => void) => {
      const aw = this.docs.awareness(docId);
      if (!aw) return () => {};
      aw.on("change", cb);
      return () => aw.off("change", cb);
    };
    let detach = attach();
    // The Awareness object is replaced on epoch change; re-attach.
    const offEpoch = this.docs.epochChanged.on((id) => {
      if (id !== docId) return;
      detach();
      detach = attach();
      cb();
    });
    return () => {
      detach();
      offEpoch();
    };
  }

  /** Fires `cb` whenever a doc's content may have changed (sync, remote
   *  update, or epoch resync — the Y.Doc identity can change across calls). */
  onDocChange(docId: string, cb: () => void): () => void {
    const offs = [
      this.docs.synced.on((id) => id === docId && cb()),
      this.docs.remoteChange.on((c) => c.docId === docId && cb()),
      this.docs.epochChanged.on((id) => id === docId && cb()),
    ];
    return () => offs.forEach((off) => off());
  }

  stop(): void {
    for (const docId of [...this.openDocs]) {
      void this.closeFile(docId);
    }
    this.provider.close();
    this.docs.close();
  }

  // --- internals -------------------------------------------------------------

  private setRole(role: Role): void {
    if (role === this.role) return;
    this.role = role;
    this.roleChanged.emit(role);
  }

  /** Announce name-only presence and keep broadcasting local renewals.
   *  (y-protocols' Awareness auto-renews local state every ~15 s, firing
   *  'update' with origin 'local' — the listener rebroadcasts it, so remote
   *  peers never see us time out.) */
  private announce(docId: string): void {
    const aw = this.docs.awareness(docId);
    if (!aw) return;
    this.awarenessDetach.get(docId)?.();
    const listener = (
      _changes: { added: number[]; updated: number[]; removed: number[] },
      origin: unknown,
    ): void => {
      if (origin === "local") this.sendAwareness(docId, aw);
    };
    aw.on("update", listener);
    this.awarenessDetach.set(docId, () => aw.off("update", listener));
    aw.setLocalState({ user: { name: this.opts.displayName } });
  }

  private sendAwareness(docId: string, aw: yprotocol.Awareness): void {
    if (!this.provider.isLive()) return;
    this.provider.sendDoc(docId, yprotocol.encodeAwareness(aw, [aw.clientID]));
  }
}
